import React, { useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { useIntl } from 'react-intl'
import { useAppDispatch } from 'hooks/reduxHooks'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import { isSearchEnabled } from 'lib/search/searchUtils'
import HeadData from 'components/Common/HeadData'
import { LUXURY_ESCAPES } from 'constants/brands'
import { ANYWHERE_PLACE_ID, TYPE_PAGE_LIMIT, ANYWHERE_NAME } from 'constants/search'
import config from 'constants/config'
import { fetchListBanners } from 'actions/ContentActions'
import { fetchHotelPopularHolidayTypes } from 'actions/DestinationAlertsActions'
import { BANNER_LOCATION_HOTELS_AND_RESORTS } from 'constants/bannerLocations'
import { FLASH_HOTELS_AND_TAO_OFFERS_TYPES, HOTEL_PAGE_OFFER_TYPES } from 'constants/offerList'
import isAnyFilterApplied from 'lib/offer/filtersUtils'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import PromptCardSelector from 'components/Common/StickyPromptCard/PromptCardSelector'
import styled from 'styled-components'
import { rem } from 'polished'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import ErrorBoundary from 'components/Common/ErrorBoundary/ErrorBoundary'
import { isManualSortingEnabledFor } from 'selectors/featuresSelectors'
import { ALL_HOTEL_TYPES, OFFER_TYPE_VILLA } from 'constants/offer'
import { useGlobalHotelsSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalHotelsSearch'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import useOfferList from 'hooks/Offers/useOfferList'
import HotelExclusiveOfferCarousel from './HotelExclusiveOfferCarousel'
import HotelPageSearchHero from './HotelPageSearchHero'
import TrendingDestinations from 'home/components/TrendingDestinations/TrendingDestinations'
import useHotelAnalytics from './hooks/useHotelAnalytics'
import TopPicksForMeCarousel from 'home/components/OfferCarousels/TopPicksForMeCarousel'
import HotelValuePropositionBanner from './HotelValuePropositionBanner'
import HotelPageCategoriesCarousel from './HotelPageCategoriesCarousel'
import { usePlaceByName } from 'hooks/usePlace'
import HotelOfferCarousel from 'home/components/OfferCarousels/HotelOfferCarousel'
import { getPlaceByRegionCode } from 'constants/places'
import { GlobalSearchTrackingProvider } from 'contexts/GlobalSearch/GlobalSearchTracking'
import { isWhitelabelBrand } from 'lib/whitelabels/whitelabels'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'

export const HotelSearchContainerPaneContent = styled.div`
  padding: ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
`

interface MappedStateProps {
  windowSearch: string
  filters: App.OfferListFilters
  regionCode: string
  geoStateName?: string
  isSearchEnabled: boolean
}

interface MappedDispatchProps {
  fetchListBanners: Utils.ActionDispatcher<typeof fetchListBanners>
}

const bestValueViewAllURL = `/search?adults=2&children=none&destinationId=${ANYWHERE_PLACE_ID}&destinationName=${ANYWHERE_NAME}&sortBy=value`
const villaViewAllUrl = `/search/homes-and-villas?destinationId=${ANYWHERE_PLACE_ID}&destinationName=${ANYWHERE_NAME}&adults=2&children=none`
const limitedTimeLuxExclusiveUrl = `/search?adults=2&children=none&destinationId=${ANYWHERE_PLACE_ID}&destinationName=${ANYWHERE_NAME}&holidayTypes=Limited+Time+LUX+Exclusive`

function HotelsPage({
  filters,
  regionCode,
  isSearchEnabled,
  fetchListBanners,
  geoStateName,
}: MappedStateProps & MappedDispatchProps) {
  const isManualSortingEnabled = isManualSortingEnabledFor('hotels')
  useEffect(() => {
    fetchListBanners(BANNER_LOCATION_HOTELS_AND_RESORTS)
  }, [fetchListBanners])

  const dispatch = useAppDispatch()
  const hasFilters = isAnyFilterApplied(filters)

  const isWhitelabel = isWhitelabelBrand(config.BRAND)

  const { globalHotelsSearchDispatch, globalHotelsSearchState } = useGlobalHotelsSearchContext()

  const {
    onHotelListEvent,
    onBestValueHotelListEvent,
    onSearchEvent,
    onTrendingDestinationsListEvent,
    onTPFMListEvent,
    onHomesVillasHotelListEvent,
    onWeekendHotelListEvent,
  } = useHotelAnalytics()

  const intl = useIntl()

  const headTitle = useMemo(() => {
    if (config.BRAND === LUXURY_ESCAPES) {
      return intl.formatMessage({
        id: 'ZUtT4ef9CHCIYaNNtNVj',
        defaultMessage: 'Luxury Hotels & Resorts at the Best Prices on Earth',
        description: 'Page title for luxury hotels page on the hotel or room booking page',
      })
    }
    return intl.formatMessage({
      id: 'tsgRaKr-Sjd4U4Rlg_pR',
      defaultMessage: 'Hotels & Resorts',
      description: 'Browser page title for the hotels and resorts listing and discovery page',
    })
  }, [intl])

  const description = useMemo(() => {
    if (config.BRAND === LUXURY_ESCAPES) {
      return intl.formatMessage({
        id: 'xAyhs7U6KcIYTeN_HCRj',
        defaultMessage: 'Book exclusive special offers for luxury hotels and accommodation at insider prices. Best Price Guarantee.',
        description: 'Page description for luxury hotels page on the hotel or room booking page',
      })
    }
    return undefined
  }, [intl])

  const finalFilters: App.OfferListFilters = useMemo(() => {
    if (isManualSortingEnabled && !hasFilters && !filters.sortBy) {
      return {
        offerTypes: ALL_HOTEL_TYPES,
        sortBy: 'vertical-hotel',
        limit: TYPE_PAGE_LIMIT,
      }
    }

    const offerTypes = !hasFilters ? HOTEL_PAGE_OFFER_TYPES(regionCode) : FLASH_HOTELS_AND_TAO_OFFERS_TYPES
    if (config.ENABLE_VILLA_RENTALS) {
      offerTypes.push(OFFER_TYPE_VILLA)
    }

    return {
      ...filters,
      offerTypes,
      frontPageVisibleOnly: true,
      limit: TYPE_PAGE_LIMIT,
      sortBy: filters.sortBy ?? 'homepage',
    }
  }, [filters, hasFilters, isManualSortingEnabled, regionCode])
  const geoStatePlace = usePlaceByName(geoStateName)

  const userPlace = useMemo(() => {
    // if regionCode is AU, use geoStatePlace if it exists
    if (regionCode === 'AU' && geoStatePlace) {
      return geoStatePlace
    }
    return getPlaceByRegionCode(regionCode)
  }, [geoStatePlace, regionCode])

  const userPlaceViewURL = `/search?adults=2&children=none&destinationId=${userPlace.id}&destinationName=${userPlace.name}`

  const flashOfferList = useOfferList(finalFilters)

  useEffect(() => {
    dispatch(fetchHotelPopularHolidayTypes())
  }, [dispatch])

  const fullTitle = useMemo(() => {
    return intl.formatMessage(
      {
        id: 'xLhIY_pmKw0VNw0zqvkW',
        defaultMessage: '{headTitle} - {brandTitle} {regionCode}',
        description: 'Full page title with brand and region code on the offer types landing page',
      },
      {
        headTitle,
        brandTitle: config.title,
        regionCode,
      },
    )
  }, [intl, headTitle, regionCode])

  return <AnalyticsPageContext.Provider value={AnalyticsPage.verticalPage}>
    <div>
      <HeadData title={fullTitle} description={description} />
      <ErrorBoundary fallback={null}>
        <PromptCardSelector tripReengagement />
      </ErrorBoundary>
      <GlobalSearchContextProvider
        state={globalHotelsSearchState}
        dispatch={globalHotelsSearchDispatch}
      >
        {(isSearchEnabled &&
          <GlobalSearchTrackingProvider onEvent={onSearchEvent}>
            <HotelPageSearchHero floatingSearchBar={!flashOfferList.fetching && !flashOfferList.offerIds.length} />
          </GlobalSearchTrackingProvider>)}
        <HotelExclusiveOfferCarousel filters={finalFilters} />

      </GlobalSearchContextProvider>
      <OfferListEventsProvider
        onListEvent={onTrendingDestinationsListEvent}
      >
        <TrendingDestinations hideFilterOptions />
      </OfferListEventsProvider>
      {(!isWhitelabel && !isAgentHubEnabled &&
        <OfferListEventsProvider onListEvent={onTPFMListEvent}>
          <TopPicksForMeCarousel onHotelListEvent={onHotelListEvent} viewAllUrl={limitedTimeLuxExclusiveUrl} />
        </OfferListEventsProvider>
      )}
      <OfferListEventsProvider onListEvent={onBestValueHotelListEvent}>
        <HotelOfferCarousel
          streamList
          title={intl.formatMessage(
            {
              id: 'VR-p-PfEKMNzVzg7RPqd',
              defaultMessage: 'Best value <i>hotel</i> offers',
              description: 'Title for best value hotel carousel with italicized hotel',
            },
            {
              i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
            },
          )}
          sortBy="value"
          viewAllUrl={bestValueViewAllURL}
          offerTypes={ALL_HOTEL_TYPES}
        />
      </OfferListEventsProvider>
      <HotelPageCategoriesCarousel />
      <HotelValuePropositionBanner />
      <OfferListEventsProvider onListEvent={onWeekendHotelListEvent}>
        <HotelOfferCarousel
          title={intl.formatMessage(
            {
              id: 'SNfjXvC2U5fXbcEcFf3q',
              defaultMessage: 'Best weekend <i>escapes</i>',
              description: 'Title for best weekend escapes carousel with italicized escapes',
            },
            {
              i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
            },
          )}
          offerTypes={ALL_HOTEL_TYPES}
          viewAllUrl={userPlaceViewURL}
          destinationId={userPlace.id}
        />
      </OfferListEventsProvider>
      {(!isWhitelabel &&
        <OfferListEventsProvider onListEvent={onHomesVillasHotelListEvent}>
          <HotelOfferCarousel
            title={intl.formatMessage(
              {
                id: 'CBAzI7HidFCjkNBsVsol',
                defaultMessage: 'Best <i>homes & villas</i> offers',
                description: 'Title for best homes & villas carousel with italicized homes & villas',
              },
              {
                i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
              },
            )}
            viewAllUrl={villaViewAllUrl}
            offerTypes={[OFFER_TYPE_VILLA]}
          />
        </OfferListEventsProvider>
      )}
    </div>
  </AnalyticsPageContext.Provider>
}

const mapStateToProps = (state: App.State): MappedStateProps => ({
  windowSearch: state.router.location.search,
  filters: getUrlOfferListFilters(state),
  regionCode: state.geo.currentRegionCode,
  geoStateName: state.geo.geoStateName,
  isSearchEnabled: isSearchEnabled(),
})

const mapDispatchToProps: MappedDispatchProps = {
  fetchListBanners,
}

export default connect(mapStateToProps, mapDispatchToProps)(HotelsPage)
